import React from "react";

import { useSelector } from "react-redux";

import { getNvdDeclarationWarnings } from "components/Chip";

import { ConsignmentPermissions } from "constants/permissions";

import { caseInsensitiveCompare } from "lib/compare";
import { formatConsignmentLabel } from "lib/consignments";
import { hasPermission } from "lib/permissions";

import {
  getBusinesses,
  getConsignments,
  getCurrentSpeciesId,
  getProperties,
  selectAgencyByConsignmentIdLookup,
  selectAgencyByDeploymentSaleIdLookup,
  selectCurrentDeploymentSales,
  selectUnknownBusinessIds,
  selectWarningsByConsignmentIdLookup,
} from "selectors";

export interface ConsignmentAutocompleteOption {
  agency_name?: string;
  isConsignment?: boolean;
  isKnown?: boolean;
  label: string;
  livestockAgencyCode?: string | null;
  nvd?: string | null;
  pic?: string | null;
  quantity?: number | string | null;
  value: number | string;
  vendorName?: string | null;
  vendorNumber?: string | null;
  warnings?: string[];
}

export interface UseConsignmentAutocompleteOptionsProps {
  fieldValue: number | string | null;
  includeCreateUnknownConsignmentOptions?: boolean;
  includeSelectedConsignmentWarnings?: boolean;
  limitToCanAddSaleLotPermission?: boolean;
  optionLabelMode?: "detailed" | "inline";
  showAgencyName?: boolean;
  showConsignmentCode?: boolean;
  sortKnownConsignmentsFirst?: boolean;
}

interface Consignment {
  declaration?: unknown;
  deployment_sale?: number | string | null;
  id: string;
  livestock_agency_code?: string | null;
  NVD?: string | null;
  permissions?: unknown;
  quantity?: number | string | null;
  vendor_id?: number | string | null;
  vendor_property_id?: number | string | null;
  vendorNumber?: string | null;
}

interface Business {
  name?: string | null;
}

interface DeploymentSale {
  deployment_sale_id: number | string;
  livestock_agency_id: number;
}

interface Property {
  PIC?: string | null;
}

type BusinessLookup = Record<string, Business | undefined>;
type ConsignmentLookup = Record<string, Consignment | undefined>;
type DeploymentSaleLookup = Record<string, DeploymentSale | undefined>;
type PropertyLookup = Record<string, Property | undefined>;
type StringArrayLookup = Record<string, string[] | undefined>;

const createDetailedConsignmentLabel = (
  consignment: Consignment,
  vendor?: Business,
  property?: Property,
): string => {
  let label = "";
  if (consignment.livestock_agency_code) {
    label += `${consignment.livestock_agency_code} `;
  }
  if (consignment.vendorNumber) {
    label += `${consignment.vendorNumber} `;
  }
  if (vendor?.name) {
    label += `${vendor.name} `;
  }
  if (consignment.quantity) {
    label += `- Hd: ${consignment.quantity} `;
  }
  if (consignment.NVD) {
    label += `- NVD: ${consignment.NVD} `;
  }
  if (property) {
    label += `- PIC: ${property.PIC} `;
  }
  return label;
};

export const useConsignmentAutocompleteOptions = ({
  fieldValue,
  includeCreateUnknownConsignmentOptions = false,
  includeSelectedConsignmentWarnings = false,
  limitToCanAddSaleLotPermission = false,
  optionLabelMode = "detailed",
  showAgencyName = true,
  showConsignmentCode = true,
  sortKnownConsignmentsFirst = false,
}: UseConsignmentAutocompleteOptionsProps): ConsignmentAutocompleteOption[] => {
  const consignmentsById = useSelector(getConsignments) as ConsignmentLookup;
  const businessesById = useSelector(getBusinesses) as BusinessLookup;
  const propertiesById = useSelector(getProperties) as PropertyLookup;
  const speciesId = useSelector(getCurrentSpeciesId);
  const deploymentSales = useSelector(
    selectCurrentDeploymentSales,
  ) as DeploymentSaleLookup;
  const agencyByConsignmentIdLookup = useSelector(
    selectAgencyByConsignmentIdLookup,
  ) as BusinessLookup;
  const unknownBusinessIds = useSelector(selectUnknownBusinessIds) as Array<
    number | string | null | undefined
  >;
  const agencyByDeploymentSaleIdLookup = useSelector(
    selectAgencyByDeploymentSaleIdLookup,
  ) as BusinessLookup;
  const warningsByConsignmentIdLookup = useSelector(
    selectWarningsByConsignmentIdLookup,
  ) as StringArrayLookup;

  return React.useMemo(() => {
    const consignments = Object.values(consignmentsById).filter(
      Boolean,
    ) as Consignment[];
    const options = consignments
      .filter(
        consignment =>
          !limitToCanAddSaleLotPermission ||
          hasPermission(consignment, ConsignmentPermissions.canAddSaleLot) ||
          fieldValue === consignment.id,
      )
      .map<ConsignmentAutocompleteOption>(consignment => {
        const vendor = consignment.vendor_id
          ? businessesById[String(consignment.vendor_id)]
          : undefined;
        const property = consignment.vendor_property_id
          ? propertiesById[String(consignment.vendor_property_id)]
          : undefined;
        const vendorName = vendor?.name || "";
        const agencyName =
          agencyByConsignmentIdLookup[String(consignment.id)]?.name ||
          "Loading Agencies";
        const nvdWarnings = getNvdDeclarationWarnings(
          consignment.declaration ? [consignment.declaration] : [],
          speciesId,
        ) as string[];
        const selectedWarnings =
          includeSelectedConsignmentWarnings && fieldValue === consignment.id
            ? warningsByConsignmentIdLookup[String(consignment.id)] || []
            : [];
        const consignmentWithVendorName = {
          ...consignment,
          vendorName,
        };

        return {
          value: consignment.id,
          isConsignment: true,
          label:
            optionLabelMode === "inline"
              ? formatConsignmentLabel(
                  consignmentWithVendorName,
                  agencyName,
                  showConsignmentCode,
                  showAgencyName,
                )
              : createDetailedConsignmentLabel(consignment, vendor, property),
          agency_name: agencyName,
          livestockAgencyCode: consignment.livestock_agency_code,
          nvd: consignment.NVD,
          pic: property?.PIC,
          quantity: consignment.quantity,
          isKnown: !unknownBusinessIds.includes(consignment.vendor_id),
          vendorName,
          vendorNumber: consignment.vendorNumber,
          warnings: [...nvdWarnings, ...selectedWarnings],
        };
      })
      .sort((a, b) => {
        if (sortKnownConsignmentsFirst && a.isKnown !== b.isKnown) {
          return Number(b.isKnown) - Number(a.isKnown);
        }
        if (optionLabelMode === "detailed") {
          return caseInsensitiveCompare(a.vendorName || "", b.vendorName || "");
        }
        return caseInsensitiveCompare(a.label, b.label);
      });

    if (includeCreateUnknownConsignmentOptions) {
      const deploymentSaleIdsWithUnknownConsignments = consignments
        .filter(consignment =>
          unknownBusinessIds.includes(consignment.vendor_id),
        )
        .reduce<Set<number | string | null | undefined>>((acc, consignment) => {
          acc.add(consignment.deployment_sale);
          return acc;
        }, new Set());
      (Object.values(deploymentSales).filter(Boolean) as DeploymentSale[])
        .filter(
          deploymentSale =>
            !deploymentSaleIdsWithUnknownConsignments.has(
              deploymentSale.deployment_sale_id,
            ),
        )
        .forEach(deploymentSale => {
          options.unshift({
            value: deploymentSale.livestock_agency_id,
            label: `Add Unknown Business Consignment For: ${
              agencyByDeploymentSaleIdLookup[deploymentSale.deployment_sale_id]
                ?.name || "Loading Agencies..."
            }`,
            isConsignment: false,
          });
        });
    }

    return options;
  }, [
    agencyByConsignmentIdLookup,
    agencyByDeploymentSaleIdLookup,
    businessesById,
    consignmentsById,
    deploymentSales,
    fieldValue,
    includeCreateUnknownConsignmentOptions,
    includeSelectedConsignmentWarnings,
    limitToCanAddSaleLotPermission,
    optionLabelMode,
    propertiesById,
    showAgencyName,
    showConsignmentCode,
    sortKnownConsignmentsFirst,
    speciesId,
    unknownBusinessIds,
    warningsByConsignmentIdLookup,
  ]);
};
